import { useDialogViewTracking } from '@/hooks/use-dialog-view-tracking';
import { sxAppend } from '@/libs/material/sx';
import { isIOS } from '@/utils/browser';
import { EnumDialogName } from '@calm/evexia-analytics';
import {
    RewardPartnerRewardDetail,
    RewardRewardsDetailsResponse,
} from '@calm/evexia-web-sdk';
import { SxProps, Theme } from '@rhg/libs/material';
import { DialogButton } from '@rhg/libs/material/simple-dialog/types';
import { createContext, ReactNode, useCallback, useState } from 'react';
import JoinDialog from './dialog';
import { RewardDetailsDialog } from './reward-details-dialog';

export const DEFAULT_TIMEOUT = 2000;
export type JoinDialogButton = Omit<DialogButton, 'onClick'>;

export interface JoinDialogState {
    joinDialog: (options: JoinDialogOptions) => void;
    isOpen: boolean;
}
export const JoinDialogContext = createContext<JoinDialogState>(null!);

export interface JoinDialogTrackProps {
    programId?: string;
    programName?: string;
}

export interface JoinDialogOptions {
    trackDialog?: EnumDialogName;
    title?: string;
    sx?: SxProps<Theme>;
    onPlayClick: () => void;
    rewardsDetails?: RewardRewardsDetailsResponse;
    trackProps?: JoinDialogTrackProps;
}

interface Props {
    children: ReactNode;
}

const styleIOSFix = {
    // Force dialog to render at top of viewport on iOS
    '& .MuiDialog-container': {
        alignItems: 'flex-start',
    },
    '& .MuiDialog-paper': {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        margin: 0,
        maxHeight: '100vh',
        height: '100vh',
    },
};

export const JoinDialogProvider = ({ children }: Props) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isRewardDetailsOpen, setIsRewardDetailsOpen] = useState(false);
    const [displayedReward, setDisplayedReward] =
        useState<RewardPartnerRewardDetail | null>(null);
    const [options, setOptions] = useState<JoinDialogOptions | undefined>(
        undefined
    );

    const onClose = useCallback(() => {
        setIsOpen(false);
    }, []);

    const handleDetailClose = useCallback(() => {
        setIsRewardDetailsOpen(false);
        setDisplayedReward(null);
    }, []);
    const handleClick = useCallback(() => {
        // Handle button click metrics here
        setIsOpen(false);
        options?.onPlayClick();
    }, [options]);

    const handleRewardClick = useCallback(
        (reward: RewardPartnerRewardDetail) => {
            setDisplayedReward(reward);
            setIsRewardDetailsOpen(true);
        },
        []
    );

    const joinDialog = useCallback((options?: JoinDialogOptions) => {
        setOptions(options);
        setIsOpen(true);
    }, []);

    const shouldUnmountJoinDialog = isIOS() && isRewardDetailsOpen;

    const shouldSkipTracking = useDialogViewTracking({
        isOpen,
        shouldUnmount: shouldUnmountJoinDialog,
    });

    return (
        <>
            <JoinDialogContext.Provider value={{ joinDialog, isOpen }}>
                {children}
            </JoinDialogContext.Provider>
            {/*
                Unmount JoinDialog when reward details is open on iOS to prevent iOS Safari layout bugs when two dialogs are present.
                Explicit positioning styles ensure both dialogs render at top of viewport on iOS.
                Pass skipDialogViewedTracking to prevent duplicate analytics events when dialog remounts.
            */}
            {!shouldUnmountJoinDialog && (
                <JoinDialog
                    isOpen={isOpen}
                    onClose={onClose}
                    skipDialogViewedTracking={shouldSkipTracking}
                    options={{
                        ...options,
                        sx: sxAppend(options?.sx, styleIOSFix),
                    }}
                    handleRewardClick={handleRewardClick}
                    handleClick={handleClick}
                />
            )}
            {isRewardDetailsOpen && displayedReward && (
                <RewardDetailsDialog
                    reward={displayedReward}
                    onClose={handleDetailClose}
                    sx={styleIOSFix}
                />
            )}
        </>
    );
};
